import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { StrategyRecommendation, TickerAnalysis, ScannerContext } from '../types';
import { TrendingUp, TrendingDown, Minus, Target, AlertTriangle, ArrowRight, Clock, Info, Compass, Rocket } from 'lucide-react';

interface StrategyPanelProps {
  strategies: StrategyRecommendation[];
  bestStrategy: StrategyRecommendation | null;
  status: 'TRADE' | 'NO_TRADE' | 'CAUTION';
  currentPrice?: number;
  ticker?: string;
  analysis?: TickerAnalysis;
}

export const StrategyPanel: React.FC<StrategyPanelProps> = ({
  strategies,
  bestStrategy,
  status,
  currentPrice = 0,
  ticker,
  analysis,
}) => {
  const navigate = useNavigate();
  const [showAlternatives, setShowAlternatives] = useState(false);
  const [selectedStrategy, setSelectedStrategy] = useState<StrategyRecommendation | null>(bestStrategy);

  const displayStrategy = selectedStrategy || bestStrategy;
  const alternativeStrategies = strategies.filter(s => s !== displayStrategy);

  const buildScannerContext = (): ScannerContext | null => {
    if (!displayStrategy || !ticker || !analysis) return null;

    const ctx: ScannerContext = {
      ticker,
      currentPrice,
      score: analysis.scores.normalizedTotal,
      status: analysis.status,
      statusReason: analysis.statusReason,
      direction: displayStrategy.direction,
      strategyName: displayStrategy.name,
      legs: displayStrategy.legs,
      maxProfit: displayStrategy.maxProfit,
      maxLoss: displayStrategy.maxLoss,
      breakeven: displayStrategy.breakeven,
      probabilityOfProfit: displayStrategy.probabilityOfProfit,
      riskRewardRatio: displayStrategy.riskRewardRatio,
      creditDebit: displayStrategy.creditDebit,
      warnings: displayStrategy.warnings,
      ivRank: analysis.optionsData.ivRank,
      riskFlags: [
        ...analysis.riskFlags.warningFlags,
        ...analysis.riskFlags.criticalFlags,
      ],
      // Full 7-step breakdown
      stepScores: {
        step1_sentiment: { score: analysis.scores.step1_sentiment.points, max: analysis.scores.step1_sentiment.maxPoints, passed: analysis.scores.step1_sentiment.passed },
        step2_technicals: { score: analysis.scores.step2_technicals.points, max: analysis.scores.step2_technicals.maxPoints, passed: analysis.scores.step2_technicals.passed },
        step3_options: { score: analysis.scores.step3_options.points, max: analysis.scores.step3_options.maxPoints, passed: analysis.scores.step3_options.passed },
        step4_risk: { score: analysis.scores.step4_risk.points, max: analysis.scores.step4_risk.maxPoints, passed: analysis.scores.step4_risk.passed },
        step5_greeks: { score: analysis.scores.step5_greeks.points, max: analysis.scores.step5_greeks.maxPoints, passed: analysis.scores.step5_greeks.passed },
        step6_entry: { score: analysis.scores.step6_entry.points, max: analysis.scores.step6_entry.maxPoints, passed: analysis.scores.step6_entry.passed },
        step7_review: { score: analysis.scores.step7_review.points, max: analysis.scores.step7_review.maxPoints, passed: analysis.scores.step7_review.passed },
        step8_sizing: { score: analysis.scores.step8_sizing.points, max: analysis.scores.step8_sizing.maxPoints, passed: analysis.scores.step8_sizing.passed },
      },
      analyzedAt: analysis.analyzedAt,
    };

    // Add volatility metrics if available
    if (analysis.volMetrics) {
      ctx.volEdge = analysis.volMetrics.volEdge;
      ctx.volEdgeSignal = analysis.volMetrics.edgeSignal;
      ctx.volEdgeStrength = analysis.volMetrics.edgeStrength;
      ctx.regime = analysis.volMetrics.regime.state;
      ctx.regimeConfidence = analysis.volMetrics.regime.confidence;
      ctx.zScore = analysis.volMetrics.zScore20d;
      ctx.ewmaForecast = analysis.volMetrics.ewmaForecast;
      ctx.rv20d = analysis.volMetrics.rv20d;
      ctx.kellyContracts = analysis.volMetrics.kellyContracts;
    }

    return ctx;
  };

  if (!displayStrategy) return null;

  // --- VISUALIZATION HELPERS ---
  const isBullish = displayStrategy.direction === 'BULLISH';
  const isBearish = displayStrategy.direction === 'BEARISH';
  const isNeutral = displayStrategy.direction === 'NEUTRAL';

  const lowerStrike = displayStrategy.legs.length > 0
    ? Math.min(...displayStrategy.legs.map(l => l.strike))
    : currentPrice - 5;
  const upperStrike = displayStrategy.legs.length > 0
    ? Math.max(...displayStrategy.legs.map(l => l.strike))
    : currentPrice + 5;
  
  // Calculate simple progress for the current price relative to strikes
  // 0% = far below lower strike, 100% = far above upper strike
  const range = upperStrike - lowerStrike;
  const pricePosition = currentPrice > 0 
    ? ((currentPrice - (lowerStrike - range)) / (range * 3)) * 100 
    : 50;
  const clampedPricePos = Math.max(10, Math.min(90, pricePosition));

  return (
    <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden shadow-2xl">
      
      {/* HEADER: Strategy Selection */}
      <div className="px-6 py-4 bg-slate-950/50 border-b border-slate-800 flex flex-col md:flex-row justify-between items-center gap-4">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${
            isBullish ? 'bg-emerald-500/20 text-emerald-400' :
            isBearish ? 'bg-red-500/20 text-red-400' :
            'bg-blue-500/20 text-blue-400'
          }`}>
            {isBullish ? <TrendingUp size={20} /> : isBearish ? <TrendingDown size={20} /> : <Minus size={20} />}
          </div>
          <div>
            <div className="text-sm text-slate-300 font-bold uppercase tracking-wider">Recommended Strategy</div>
            <h2 className="text-xl font-bold text-white flex items-center gap-2">
              {displayStrategy.name}
              {selectedStrategy === bestStrategy && (
                <span className="text-xs bg-cyan-500/20 text-cyan-400 px-2 py-0.5 rounded border border-cyan-500/30">BEST</span>
              )}
            </h2>
          </div>
        </div>

        {alternativeStrategies.length > 0 && (
          <div className="relative">
            <button 
              onClick={() => setShowAlternatives(!showAlternatives)}
              className="text-xs font-bold text-slate-300 hover:text-white flex items-center gap-1 bg-slate-800 px-3 py-1.5 rounded transition-colors"
            >
              See {alternativeStrategies.length} Alternatives {showAlternatives ? '▲' : '▼'}
            </button>
            {showAlternatives && (
              <div className="absolute right-0 top-full mt-2 w-64 bg-slate-800 border border-slate-700 rounded-lg shadow-xl z-50 overflow-hidden">
                {alternativeStrategies.map((strat, i) => (
                  <button
                    key={i}
                    onClick={() => { setSelectedStrategy(strat); setShowAlternatives(false); }}
                    className="w-full text-left px-4 py-3 hover:bg-slate-700 border-b border-slate-700/50 last:border-0"
                  >
                    <div className="text-white font-bold text-sm">{strat.name}</div>
                    <div className="text-sm text-slate-300 flex justify-between">
                      <span>{strat.riskProfile} Risk</span>
                      <span className={strat.suitabilityScore > 80 ? 'text-emerald-400' : 'text-yellow-400'}>
                        {strat.suitabilityScore}/100
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="p-6 grid grid-cols-1 lg:grid-cols-12 gap-8">
        
        {/* LEFT COLUMN: Visuals & Logic (7 cols) */}
        <div className="lg:col-span-7 space-y-8">
          
          {/* 1. PAYOFF VISUALIZATION - Uses real strike positions */}
          <div className="bg-slate-950 rounded-xl p-5 border border-slate-800 relative overflow-hidden">
            <div className="text-sm text-slate-300 font-bold uppercase mb-4 flex justify-between">
              <span>Payoff at Expiry</span>
              <span className={`${isBullish ? 'text-emerald-400' : isBearish ? 'text-red-400' : 'text-blue-400'}`}>
                {isBullish ? 'Bullish Outlook' : isBearish ? 'Bearish Outlook' : 'Neutral Outlook'}
              </span>
            </div>

            {/* SVG Chart with real strike positions */}
            {(() => {
              const pad = range * 0.6;
              const chartMin = lowerStrike - pad;
              const chartMax = upperStrike + pad;
              const chartSpan = chartMax - chartMin;
              const toX = (p: number) => Math.max(0, Math.min(100, ((p - chartMin) / chartSpan) * 100));
              const lX = toX(lowerStrike);
              const uX = toX(upperStrike);
              const pX = toX(currentPrice);
              const profitY = 10;
              const lossY = 40;

              return (
                <div className="h-32 w-full relative">
                  <svg viewBox="0 0 100 50" className="w-full h-full overflow-visible" preserveAspectRatio="none">
                    <line x1="0" y1="25" x2="100" y2="25" stroke="#334155" strokeWidth="0.5" strokeDasharray="2" />
                    {/* Profit zone shading */}
                    {isBullish && <rect x={uX} y={profitY - 2} width={100 - uX} height={17} fill="#10b98118" rx="1" />}
                    {isBearish && <rect x="0" y={profitY - 2} width={lX} height={17} fill="#f43f5e18" rx="1" />}
                    {isNeutral && <rect x={lX} y={profitY - 2} width={uX - lX} height={17} fill="#3b82f618" rx="1" />}
                    {/* Payoff curve */}
                    {isNeutral ? (
                      <path d={`M0,${lossY} L${lX},${lossY} L${lX},${profitY} L${uX},${profitY} L${uX},${lossY} L100,${lossY}`} fill="none" stroke="#3b82f6" strokeWidth="2" />
                    ) : isBullish ? (
                      <path d={`M0,${lossY} L${lX},${lossY} L${uX},${profitY} L100,${profitY}`} fill="none" stroke="#10b981" strokeWidth="2" />
                    ) : (
                      <path d={`M0,${profitY} L${lX},${profitY} L${uX},${lossY} L100,${lossY}`} fill="none" stroke="#f43f5e" strokeWidth="2" />
                    )}
                    <circle cx={lX} cy={isBullish ? lossY : (isNeutral ? lossY : profitY)} r="2" fill="#cbd5e1" />
                    <circle cx={uX} cy={isBullish ? profitY : lossY} r="2" fill="#cbd5e1" />
                    <line x1={pX} y1="0" x2={pX} y2="50" stroke="#fbbf24" strokeWidth="1" strokeDasharray="1" />
                    <circle cx={pX} cy={25} r="2.5" fill="#fbbf24" />
                  </svg>
                  <div className="absolute top-0 left-0 w-full h-full pointer-events-none text-xs text-slate-300">
                    <div style={{ left: `${lX}%`, top: isBullish ? '80%' : '5%' }} className="absolute -translate-x-1/2">${lowerStrike}</div>
                    <div style={{ left: `${uX}%`, top: isBullish ? '5%' : '80%' }} className="absolute -translate-x-1/2">${upperStrike}</div>
                    <div style={{ left: `${pX}%`, top: '42%' }} className="absolute -translate-x-1/2 text-amber-400 font-bold">${currentPrice.toFixed(0)}</div>
                  </div>
                </div>
              );
            })()}

            <div className="mt-2 flex justify-between text-sm text-slate-300">
              <div className="text-red-400 font-bold">Max Loss: ${displayStrategy.maxLoss.toFixed(0)}</div>
              <div className="text-emerald-400 font-bold">Max Profit: ${displayStrategy.maxProfit.toFixed(0)}</div>
            </div>
          </div>

          {/* 2. WHY THIS TRADE */}
          <div>
            <div className="text-sm text-slate-300 font-bold uppercase mb-3">Strategy Logic</div>
            <div className="grid gap-2">
              {displayStrategy.suitabilityReasons.map((reason, i) => (
                <div key={i} className="flex items-start gap-3 text-sm text-slate-300 bg-slate-800/30 p-2 rounded">
                  <ArrowRight size={14} className="mt-1 text-cyan-400 shrink-0" />
                  {reason}
                </div>
              ))}
            </div>
          </div>

          {/* 3. STRATEGY EXPLANATION */}
          {displayStrategy.strategyExplanation && (
            <div className="bg-slate-950/50 rounded-xl p-4 border border-slate-800">
              <div className="text-sm text-slate-300 font-bold uppercase mb-2 flex items-center gap-2">
                <Info size={12} className="text-blue-400" />
                Why This Strategy
              </div>
              <p className="text-sm text-slate-300 leading-relaxed">
                {displayStrategy.strategyExplanation}
              </p>
            </div>
          )}

          {/* 4. DIRECTION REASONING */}
          {displayStrategy.directionReasoning && (
            <div className="bg-slate-950/50 rounded-xl p-4 border border-slate-800">
              <div className="text-sm text-slate-300 font-bold uppercase mb-2 flex items-center gap-2">
                <Compass size={12} className="text-purple-400" />
                Market Direction Analysis
              </div>
              <p className="text-sm text-slate-300 leading-relaxed">
                {displayStrategy.directionReasoning}
              </p>
            </div>
          )}

          {/* 5. EXPIRY REASONING */}
          {displayStrategy.expiryReasoning && (
            <div className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 rounded-xl p-4 border border-amber-500/20">
              <div className="text-xs text-amber-400 font-bold uppercase mb-2 flex items-center gap-2">
                <Clock size={12} />
                Expiry Selection ({displayStrategy.legs[0]?.expiry || 'N/A'})
              </div>
              <p className="text-sm text-slate-300 leading-relaxed">
                {displayStrategy.expiryReasoning}
              </p>
            </div>
          )}
        </div>

        {/* RIGHT COLUMN: Execution Data (5 cols) */}
        <div className="lg:col-span-5 space-y-6">
          
          {/* Trade Structure (Legs) */}
          <div className="bg-black/20 rounded-xl p-4 border border-slate-800/50">
            <div className="text-sm text-slate-300 font-bold uppercase mb-3">Structure Setup</div>
            <div className="space-y-2">
              {displayStrategy.legs.map((leg, i) => (
                <div key={i} className="flex items-center justify-between p-2 rounded bg-slate-900 border border-slate-800">
                  <div className="flex items-center gap-3">
                    <span className={`text-xs font-bold px-2 py-1 rounded ${leg.action === 'BUY' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}`}>
                      {leg.action}
                    </span>
                    <span className="text-sm font-bold text-white font-mono">{leg.strike} {leg.type}</span>
                  </div>
                  <div className="text-sm text-slate-300 font-mono">
                    {leg.dte} DTE
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-3 pt-3 border-t border-slate-800 flex justify-between text-xs">
              <span className="text-slate-300">Cost Basis</span>
              <span className={`font-bold font-mono ${displayStrategy.creditDebit === 'CREDIT' ? 'text-emerald-400' : 'text-white'}`}>
                {displayStrategy.creditDebit === 'CREDIT' ? 'Receive' : 'Pay'} ${(displayStrategy.maxProfit / 100).toFixed(2)}
              </span>
            </div>
          </div>

          {/* Key Stats Grid */}
          <div className="grid grid-cols-2 gap-3">
             <div className="bg-slate-800/50 p-3 rounded-lg border border-slate-700/50">
               <div className="text-sm text-slate-300 mb-1">Win Probability</div>
               <div className="text-xl font-bold text-cyan-400">{(displayStrategy.probabilityOfProfit * 100).toFixed(0)}%</div>
             </div>
             <div className="bg-slate-800/50 p-3 rounded-lg border border-slate-700/50">
               <div className="text-sm text-slate-300 mb-1">Risk/Reward</div>
               <div className="text-xl font-bold text-white">1:{displayStrategy.riskRewardRatio.toFixed(1)}</div>
             </div>
             <div className="bg-slate-800/50 p-3 rounded-lg border border-slate-700/50">
               <div className="text-sm text-slate-300 mb-1">Breakeven</div>
               <div className="text-lg font-bold text-white font-mono">
                 ${Array.isArray(displayStrategy.breakeven) ? displayStrategy.breakeven[0].toFixed(2) : displayStrategy.breakeven.toFixed(2)}
               </div>
             </div>
             <div className="bg-slate-800/50 p-3 rounded-lg border border-slate-700/50">
               <div className="text-sm text-slate-300 mb-1">Return on Risk</div>
               <div className="text-lg font-bold text-emerald-400 font-mono">
                 {((displayStrategy.maxProfit / displayStrategy.maxLoss) * 100).toFixed(0)}%
               </div>
               <div className="text-xs text-slate-300 mt-0.5">at expiry, if held</div>
             </div>
          </div>

          {/* Warning Flag */}
          {displayStrategy.warnings.length > 0 && (
            <div className="flex items-start gap-2 text-xs text-amber-400 bg-amber-500/10 p-3 rounded border border-amber-500/20">
              <AlertTriangle size={14} className="mt-0.5 shrink-0" />
              <div>
                <span className="font-bold block mb-1">Risk Warning</span>
                {displayStrategy.warnings[0]}
              </div>
            </div>
          )}

          {/* Ask Moonshot CTA */}
          <button
            onClick={() => {
              const ctx = buildScannerContext();
              navigate('/moonshot', { state: { scannerContext: ctx } });
            }}
            className="block w-full p-4 bg-gradient-to-r from-purple-600/20 to-pink-600/20 hover:from-purple-600/30 hover:to-pink-600/30 border border-purple-500/30 hover:border-purple-500/50 rounded-xl transition-all group text-left"
          >
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg">
                <Rocket size={18} className="text-white" />
              </div>
              <div className="flex-1">
                <div className="text-sm font-bold text-white group-hover:text-purple-200">Ask Moonshot</div>
                <div className="text-sm text-slate-300">Get trade management advice{ticker ? ` for ${ticker}` : ''}</div>
              </div>
              <ArrowRight size={16} className="text-purple-400 group-hover:translate-x-1 transition-transform" />
            </div>
          </button>

        </div>
      </div>
    </div>
  );
};
